import { useEffect, useState } from "react";
import api, { apiError, fmtMXN } from "@/lib/api";
import { ColumnFilterRow, SortableHeaderRow, TableToolbar, useTableTools } from "@/components/TableFilters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const empty = { nombre: "", sku: "", descripcion: "", unidad: "Pieza", impuesto: "iva16", precio_venta: 0, costo_adquisicion: 0, stock_actual: 0, stock_minimo: 0, proveedor_urls: [] };

const PIVA = [["iva16", "IVA 16%"], ["exento", "Exento"]];
const COLUMNS = [
  { key: "nombre", label: "Producto", accessor: (p) => p.nombre, placeholder: "Producto" },
  { key: "sku", label: "SKU", accessor: (p) => p.sku, placeholder: "SKU" },
  { key: "precio", label: "Precio", accessor: (p) => p.precio_venta, placeholder: "Precio" },
  { key: "costo", label: "Costo", accessor: (p) => p.costo_adquisicion, placeholder: "Costo" },
  { key: "stock", label: "Stock", accessor: (p) => p.stock_actual, placeholder: "Stock" },
  { key: "iva", label: "IVA", type: "select", accessor: (p) => p.impuesto, options: PIVA.map(([value, label]) => ({ value, label })) },
  { key: "_a", type: "none" },
];

export default function Products() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [urls, setUrls] = useState("");
  const tools = useTableTools(rows, COLUMNS);

  const load = () => api.get("/products").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setUrls(""); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setUrls((p.proveedor_urls || []).join("\n")); setEditId(p.id); setOpen(true); };

  const save = async () => {
    const payload = { ...form, proveedor_urls: urls.split("\n").map((u) => u.trim()).filter(Boolean) };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success("Producto guardado"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold tracking-tight">Catálogo</h1><p className="text-muted-foreground text-sm">Productos, costos y punto de reorden.</p></div>
        <div className="flex items-center gap-2">
          <TableToolbar tools={tools} columns={COLUMNS} filename="catalogo" />
          <Button onClick={openNew} data-testid="new-product-btn"><Plus className="w-4 h-4 mr-2" /> Nuevo producto</Button>
        </div>
      </div>
      <Card className="rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <SortableHeaderRow columns={COLUMNS} sort={tools.sort} toggleSort={tools.toggleSort} />
            {tools.showFilters && <ColumnFilterRow columns={COLUMNS} filters={tools.filters} setFilters={tools.setFilters} />}
          </TableHeader>
          <TableBody>
            {tools.view.map((p) => (
              <TableRow key={p.id} data-testid="product-row">
                <TableCell className="font-medium">{p.nombre}</TableCell>
                <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                <TableCell>{fmtMXN(p.precio_venta)}</TableCell>
                <TableCell className="text-muted-foreground">{fmtMXN(p.costo_adquisicion)}</TableCell>
                <TableCell>{p.stock_minimo > 0 && p.stock_actual <= p.stock_minimo ? <span className="flex items-center gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" />{p.stock_actual}</span> : p.stock_actual}<span className="text-xs text-muted-foreground"> / {p.stock_minimo}</span></TableCell>
                <TableCell><Badge variant="secondary">{p.impuesto === "iva16" ? "16%" : "Exento"}</Badge></TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin productos aún.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Editar" : "Nuevo"} producto</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="product-nombre" /></div>
            <div><Label>SKU / Código *</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku" /></div>
            <div><Label>Unidad</Label>
              <Select value={form.unidad} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Pieza", "Servicio", "Caja", "Kg", "Litro", "Licencia"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Descripción</Label><Textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={2} /></div>
            <div><Label>Precio venta</Label><Input type="number" value={form.precio_venta} onChange={(e) => setForm({ ...form, precio_venta: Number(e.target.value) })} data-testid="product-precio" /></div>
            <div><Label>Costo adquisición</Label><Input type="number" value={form.costo_adquisicion} onChange={(e) => setForm({ ...form, costo_adquisicion: Number(e.target.value) })} /></div>
            <div><Label>Stock actual</Label><Input type="number" value={form.stock_actual} onChange={(e) => setForm({ ...form, stock_actual: Number(e.target.value) })} /></div>
            <div><Label>Stock mínimo (reorden)</Label><Input type="number" value={form.stock_minimo} onChange={(e) => setForm({ ...form, stock_minimo: Number(e.target.value) })} /></div>
            <div><Label>Impuesto</Label>
              <Select value={form.impuesto} onValueChange={(v) => setForm({ ...form, impuesto: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="iva16">IVA 16%</SelectItem><SelectItem value="exento">Exento</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Enlaces de proveedores web (uno por línea)</Label><Textarea value={urls} onChange={(e) => setUrls(e.target.value)} rows={3} placeholder="https://proveedor.com/producto" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-product">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
